import React, { useState, useRef, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { TacticEditorModal } from "./TacticEditorModal";
import { BasicTacticModal } from "./BasicTacticModal";
import { serializeStatements } from "./tacticModel";
import "./TacticList.css";

// CALL_TACTIC wire + target band (must match tokens.hh) — to find which tactics
// are CALLED by another (they can't be deleted; Docs/tactic-calls.txt).
const CALL_ACTION = 1030;
const TARGET_OFFSET = 100000;

/**
 * @param {{
 *   tactics: object[],
 *   loading: boolean,
 *   onCreate: (name: string) => Promise,
 *   onUpdate: (id: number, statements: object[]) => Promise,
 *   onDelete: (id: number) => Promise,
 *   assignedId: number | null,       // currently assigned tactic id (for selected player)
 *   onAssign: ((tacticId: number | null) => void) | null, // null = not in assign mode
 * }} props
 */
export function TacticList({ tactics, loading, error, canCreate = true, onCreate, onUpdate, onDelete, onCreateBasic = null, assignedId = null, onAssign = null, defaultName = "", players = [] }) {
  const { t } = useTranslation();
  const [adding, setAdding]           = useState(false);
  const [newName, setNewName]         = useState("");
  const [nameError, setNameError]     = useState("");
  const [saving, setSaving]           = useState(false);
  const [editing, setEditing]         = useState(null);
  const [copying, setCopying]         = useState(null); // source tactic id
  const [pendingDeleteId, setPendingDeleteId] = useState(null);
  const [basicOpen, setBasicOpen]     = useState(false);
  const inputRef = useRef(null);

  const assignMode  = onAssign !== null;

  // Ids of tactics that are CALLED by another of this user's tactics -> they are
  // "in use" and cannot be deleted (the backend also blocks it with a 409).
  const calledIds = useMemo(() => {
    const s = new Set();
    for (const tac of tactics) {
      const toks = serializeStatements(tac.statements || []);
      for (let i = 0; i < toks.length; i++) {
        if (toks[i] === CALL_ACTION && toks[i + 1] >= TARGET_OFFSET) s.add(toks[i + 1] - TARGET_OFFSET);
      }
    }
    return s;
  }, [tactics]);
  const atLimit     = tactics.length >= 10;

  useEffect(() => {
    if (adding) inputRef.current?.focus();
  }, [adding]);

  const existingNames = tactics.map((t) => t.name.toLowerCase());

  const handleAddConfirm = async () => {
    const trimmed = newName.trim();
    if (!trimmed) { setNameError(t("tactic.nameRequired")); return; }
    if (existingNames.includes(trimmed.toLowerCase())) { setNameError(t("tactic.nameDuplicate")); return; }
    setSaving(true);
    try {
      const tactic = await onCreate(trimmed);
      if (copying !== null) {
        const source = tactics.find((t) => t.id === copying);
        if (source) await onUpdate(tactic.id, source.statements);
        setCopying(null);
      } else if (!assignMode) {
        setEditing(tactic.id);
      }
      setNewName("");
      setAdding(false);
      setNameError("");
    } catch (err) {
      setNameError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleCopy = (tactic) => {
    setCopying(tactic.id);
    setNewName(`${tactic.name} ${t("tactic.copySuffix")}`);
    setAdding(true);
  };

  const handleAddCancel = () => { setAdding(false); setNewName(""); setNameError(""); setCopying(null); };

  const handleSave = async (id, statements) => {
    await onUpdate(id, statements);
    setEditing(null);
  };

  const editingTactic = tactics.find((t) => t.id === editing);

  if (loading) return <p className="tactic-list-empty">{t("tactic.loading")}</p>;
  if (error)   return <p className="tactic-list-empty tactic-list-error">{t("tactic.loadError")}</p>;

  return (
    <div className="tactic-list">
      <div className="tactic-list-header">
        <span className="tactic-list-title">
          {assignMode ? t("tactic.assign") : t("tactic.tactics")}
        </span>
        {!adding && !atLimit && canCreate && (
          <button className="tactic-add-btn" onClick={() => { setNewName(defaultName); setAdding(true); }}>
            + {t("tactic.new")}
          </button>
        )}
        {atLimit && <span className="tactic-limit-hint">{t("tactic.limitReached")}</span>}
      </div>

      {tactics.length === 0 && !adding && (
        <div className="tactic-empty-quickstart">
          <p className="tactic-list-empty">{t("tactic.noTactics")}</p>
          {canCreate && onCreateBasic && (
            <button className="basic-create-btn" onClick={() => setBasicOpen(true)}>
              {t("basic.createButton")}
            </button>
          )}
        </div>
      )}

      <ul className="tactic-list-items">
        {tactics.map((tactic) => {
          const isAssigned = tactic.id === assignedId;
          return (
            <li key={tactic.id} className={`tactic-list-item ${isAssigned ? "tactic-assigned" : ""}`}>
              <button
                className="tactic-name-btn"
                onClick={() => assignMode ? onAssign(isAssigned ? null : tactic.id) : setEditing(tactic.id)}
                title={assignMode ? t("tactic.clickToAssign") : t("tactic.clickToEdit")}
              >
                {tactic.name}
                {assignMode && isAssigned && <span className="tactic-assigned-badge">✓</span>}
                {!assignMode && tactic.statements.length === 0 && (
                  <span className="tactic-rule-count">{t("tactic.empty")}</span>
                )}
              </button>

              {/* In assign mode, show edit button separately */}
              {assignMode && (
                <button className="tactic-edit-btn" onClick={() => setEditing(tactic.id)} title={t("tactic.edit")}>
                  ✎
                </button>
              )}

              {!atLimit && (
                <button className="tactic-copy-btn" onClick={() => handleCopy(tactic)} title={t("tactic.copy")} disabled={adding}>
                  ⎘
                </button>
              )}

              {pendingDeleteId === tactic.id ? (
                <span className="tactic-delete-confirm">
                  <span className="tactic-delete-warn">
                    {t("tactic.deleteUsedWarn", { count: players.filter((p) => p.tacticId === tactic.id).length })}
                  </span>
                  <button className="tactic-confirm-btn" onClick={() => { setPendingDeleteId(null); Promise.resolve(onDelete(tactic.id)).catch(() => {}); }}>✓</button>
                  <button className="tactic-cancel-btn"  onClick={() => setPendingDeleteId(null)}>✕</button>
                </span>
              ) : calledIds.has(tactic.id) ? (
                <button className="tactic-delete-btn" disabled title={t("tactic.inUseByCall")}
                        style={{ opacity: 0.35, cursor: "not-allowed" }}>
                  ✕
                </button>
              ) : (
                <button className="tactic-delete-btn" onClick={() => {
                  const usedBy = players.filter((p) => p.tacticId === tactic.id).length;
                  if (usedBy > 0) setPendingDeleteId(tactic.id);
                  else Promise.resolve(onDelete(tactic.id)).catch(() => {});
                }} title={t("tactic.delete")}>
                  ✕
                </button>
              )}
            </li>
          );
        })}
      </ul>

      {adding && (
        <div className="tactic-new-row">
          <input
            ref={inputRef}
            className={`tactic-name-input ${nameError ? "input-error" : ""}`}
            value={newName}
            onChange={(e) => { setNewName(e.target.value); setNameError(""); }}
            onKeyDown={(e) => { if (e.key === "Enter") handleAddConfirm(); if (e.key === "Escape") handleAddCancel(); }}
            placeholder={t("tactic.namePlaceholder")}
            maxLength={30}
            disabled={saving}
          />
          <button className="tactic-confirm-btn" onClick={handleAddConfirm} disabled={saving}>✓</button>
          <button className="tactic-cancel-btn" onClick={handleAddCancel} disabled={saving}>✕</button>
          {nameError && <span className="tactic-name-error">{nameError}</span>}
        </div>
      )}

      {editingTactic && (
        <TacticEditorModal
          tacticId={editingTactic.id}
          name={editingTactic.name}
          statements={editingTactic.statements}
          onSave={(stmts) => handleSave(editingTactic.id, stmts)}
          onClose={() => setEditing(null)}
        />
      )}

      {basicOpen && (
        <BasicTacticModal
          defaultName={t("basic.defaultName")}
          onConfirm={onCreateBasic}
          onClose={() => setBasicOpen(false)}
        />
      )}
    </div>
  );
}
